/**
 * AssignRepModal
 * Modal to assign sales reps to an event.
 * Shows searchable position list, role selector, current assignments.
 */

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getEventSalesAssignments,
  createEventSalesAssignment,
  deleteEventSalesAssignment,
  makeEventSalesLead,
} from "../../services/salesCalendar.service";
import { getSalesPositions } from "../../services/salesTeam.service";
import { useAuth } from "../../contexts/AuthContext";
import type { AssignmentRole } from "../../types/salesCalendar.types";
import { ASSIGNMENT_ROLE_LABELS } from "../../types/salesCalendar.types";
import { POSITION_TYPE_LABELS } from "../../types/sales.types";
import Button from "../common/Button";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import Badge from "../ui/Badge";
import ConfirmDialog from "../ui/ConfirmDialog";
import { useEventLocalDates, useTimeOff } from "../../hooks/useTimeOff";
import { joinDays, timeOffSentence } from "../../services/timeOffConflicts";

interface AssignRepModalProps {
  eventId: string;
  eventTitle: string;
  onClose: () => void;
}

const AssignRepModal: React.FC<AssignRepModalProps> = ({
  eventId,
  eventTitle,
  onClose,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState<AssignmentRole>("assigned");

  // Fetch current assignments for this event
  const { data: assignments = [] } = useQuery({
    queryKey: ["event-sales-assignments", eventId],
    queryFn: () => getEventSalesAssignments(eventId),
  });

  // Fetch all active positions
  const { data: positions = [] } = useQuery({
    queryKey: ["sales-positions-active"],
    queryFn: () => getSalesPositions({ is_active: true }),
  });

  // MSR QA 10-20: warn (never block) when the picked rep has time off on one of this event's local dates.
  const { data: eventDates = [] } = useEventLocalDates(eventId);
  const { data: timeOff = {} } = useTimeOff(
    positions.map((p) => p.id),
    eventDates,
  );
  const [pendingTimeOff, setPendingTimeOff] = useState<{
    positionId: string;
    message: string;
  } | null>(null);
  const pick = (positionId: string, name: string) => {
    const off = timeOff[positionId] ?? [];
    if (off.length) {
      setPendingTimeOff({
        positionId,
        message: `${timeOffSentence(name, off)} Assign anyway?`,
      });
      return;
    }
    assignMutation.mutate(positionId);
  };

  const assignedPositionIds = new Set(
    assignments.map((a) => a.sales_position_id),
  );

  const filteredPositions = positions.filter((p) => {
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        p.assigned_name?.toLowerCase().includes(q) ||
        false ||
        p.title.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const assignMutation = useMutation({
    mutationFn: async (positionId: string) => {
      if (selectedRole === "lead") {
        // One change: an existing Lead becomes a rep and the event's Lead/Event Rep follows (migration 417). A
        // direct insert failed with the one-lead index when the event already had a Lead.
        await makeEventSalesLead(
          eventId,
          positionId,
          assignments.find((a) => a.role === "lead")?.sales_position_id ?? null,
        );
        return;
      }
      await createEventSalesAssignment(
        {
          event_id: eventId,
          sales_position_id: positionId,
          role: selectedRole,
        },
        user?.id,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["event-sales-assignments", eventId],
      });
      queryClient.invalidateQueries({ queryKey: ["sales-calendar"] });
      // GS-05: the Confirm schedule grid reads the staffing set + version
      queryClient.invalidateQueries({ queryKey: ["event-staffing", eventId] });
      // Migration 417: the list's lead is the event's Lead/Event Rep, so the event header may have changed too.
      queryClient.invalidateQueries({
        queryKey: ["events", "detail", eventId],
      });
    },
  });

  // Removing a rep asks first.
  const [pendingRemoval, setPendingRemoval] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const removeMutation = useMutation({
    mutationFn: (assignmentId: string) =>
      deleteEventSalesAssignment(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["event-sales-assignments", eventId],
      });
      queryClient.invalidateQueries({
        queryKey: ["event-sales-assignment-days", eventId],
      });
      queryClient.invalidateQueries({ queryKey: ["sales-calendar"] });
      // GS-05: the Confirm schedule grid reads the staffing set + version
      queryClient.invalidateQueries({ queryKey: ["event-staffing", eventId] });
      // Migration 417: the list's lead is the event's Lead/Event Rep, so the event header may have changed too.
      queryClient.invalidateQueries({
        queryKey: ["events", "detail", eventId],
      });
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Assign Sales Reps
            </h3>
            <p className="text-sm text-gray-500 truncate">{eventTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {(assignMutation.error || removeMutation.error) && (
          <div
            role="alert"
            className="mx-4 mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800"
          >
            {(assignMutation.error ?? removeMutation.error)?.message ??
              "That change could not be saved."}
          </div>
        )}

        {/* Current Assignments */}
        {assignments.length > 0 && (
          <div className="p-4 border-b bg-gray-50">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              Currently Assigned ({assignments.length})
            </h4>
            <div className="space-y-1.5">
              {assignments.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between bg-white rounded px-3 py-1.5"
                >
                  <div className="flex items-center gap-2">
                    <UserCircleIcon className="h-5 w-5 text-gray-300" />
                    <span className="text-sm text-gray-900">
                      {a.sales_position?.assigned_name ||
                        a.sales_position?.title ||
                        "Unknown"}
                    </span>
                    <Badge
                      color={a.role === "lead" ? "blue" : "gray"}
                      size="sm"
                    >
                      {ASSIGNMENT_ROLE_LABELS[a.role as AssignmentRole] ||
                        a.role}
                    </Badge>
                  </div>
                  <button
                    onClick={() =>
                      setPendingRemoval({
                        id: a.id,
                        name:
                          a.sales_position?.assigned_name ||
                          a.sales_position?.title ||
                          "this rep",
                      })
                    }
                    className="text-gray-400 hover:text-red-600"
                    title="Remove assignment"
                    disabled={removeMutation.isPending}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add assignments */}
        <div className="p-4 flex-1 overflow-y-auto">
          {/* Role selector + search */}
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or title..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
            <select
              value={selectedRole}
              onChange={(e) =>
                setSelectedRole(e.target.value as AssignmentRole)
              }
              className="border border-gray-300 rounded-lg px-2 py-2 text-sm"
            >
              <option value="assigned">Assigned</option>
              <option value="lead">Lead</option>
            </select>
          </div>

          {/* Position list */}
          <div className="space-y-1">
            {filteredPositions.map((pos) => {
              const isAssigned = assignedPositionIds.has(pos.id);
              return (
                <div
                  key={pos.id}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                    isAssigned
                      ? "bg-gray-50 opacity-50"
                      : "hover:bg-gray-50 cursor-pointer"
                  }`}
                  onClick={() => {
                    if (!isAssigned && !assignMutation.isPending) {
                      pick(pos.id, pos.assigned_name || pos.title);
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <UserCircleIcon className="h-5 w-5 text-gray-300" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {pos.assigned_name || "Unassigned"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {pos.title} · {POSITION_TYPE_LABELS[pos.position_type]}
                      </div>
                    </div>
                  </div>
                  {(timeOff[pos.id] ?? []).length > 0 && (
                    <span
                      className="mr-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900"
                      data-testid="time-off-badge"
                      title={`Not marked available: ${joinDays(timeOff[pos.id])}`}
                    >
                      Not available
                    </span>
                  )}
                  {isAssigned ? (
                    <Badge color="gray" size="sm">
                      Assigned
                    </Badge>
                  ) : (
                    <span className="text-xs text-blue-600">+ Add</span>
                  )}
                </div>
              );
            })}
            {filteredPositions.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">
                No matching positions
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
      <ConfirmDialog
        isOpen={pendingTimeOff !== null}
        onClose={() => setPendingTimeOff(null)}
        onConfirm={() => {
          if (pendingTimeOff) {
            assignMutation.mutate(pendingTimeOff.positionId);
          }
          setPendingTimeOff(null);
        }}
        title="This rep isn't marked available"
        message={pendingTimeOff?.message ?? ""}
        confirmText="Assign anyway"
        variant="warning"
      />
      <ConfirmDialog
        isOpen={pendingRemoval !== null}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) {
            removeMutation.mutate(pendingRemoval.id);
          }
        }}
        title="Remove rep from this event?"
        message={`${pendingRemoval?.name ?? "This rep"} will be taken off this event's Sales Reps list.`}
        confirmText="Remove"
        variant="danger"
      />
    </div>
  );
};

export default AssignRepModal;
